<script setup lang="ts">
import { computed } from 'vue'

import type { PlatformEventsPivot } from '../models/platformEvent.types'

import {
  MatrixTable,
  ReportChannelTabs,
  type ReportColumn,
  type ReportRow,
} from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import {
  Button,
  PlatformLogo,
  resolveStaticPlatformLogo,
  type PlatformLogoProps,
  type UiAsyncState,
} from '@/shared/ui'

const props = defineProps<{
  pivot: PlatformEventsPivot
  channels: string[]
  state: UiAsyncState
}>()
const activeTab = defineModel<string>('activeTab', { default: 'overall' })
defineEmits<{ retry: [] }>()
const { t } = useI18n()
const format = useFormat()

const columns = computed<ReportColumn[]>(() => [
  {
    field: 'date',
    header: t('summary.platformEvents.events'),
    frozen: true,
    sortable: false,
    resizable: false,
    width: 160,
  },
  ...props.pivot.events.map((event) => ({
    field: event,
    header: event.replaceAll('_', ' '),
    numeric: true,
    sortable: false,
    resizable: false,
    minWidth: 140,
  })),
])
const rows = computed<ReportRow[]>(() =>
  props.pivot.dates.map((date) =>
    Object.fromEntries([
      ['date', date],
      ...props.pivot.events.map((event) => [
        event,
        props.pivot.byEvent[event]?.values[date]?.event_count ?? 0,
      ]),
    ]),
  ),
)
const footerRows = computed<ReportRow[]>(() => [
  Object.fromEntries([
    ['date', t('summary.platformEvents.total')],
    ...props.pivot.events.map((event) => [event, props.pivot.byEvent[event]?.total ?? 0]),
  ]),
])

function channelFor(field: string): string {
  return props.pivot.byEvent[field]?.marketingChannel ?? ''
}

function logoProps(field: string): PlatformLogoProps {
  const platform = channelFor(field)
  const src = resolveStaticPlatformLogo(platform)
  return { platform, alt: platform, size: 18, ...(src ? { src } : {}) }
}
</script>

<template>
  <div class="flex w-full min-h-0 mt-sm">
    <ReportChannelTabs
      v-model="activeTab"
      :tabs="channels"
      :labels="{ overall: t('summary.platformEvents.overall') }"
      v-bind="{ ariaLabel: t('summary.platformEvents.channels') }"
    />
    <MatrixTable
      class="min-w-0 flex-1"
      :rows="rows"
      :footer-rows="footerRows"
      :columns="columns"
      :state="state"
      v-bind="{ ariaLabel: t('summary.platformEvents.tableLabel') }"
      max-height="37.5rem"
    >
      <template #header="{ column }">
        <span
          class="summary-events__heading flex w-full items-center gap-2xs text-primary capitalize"
          :class="{ 'summary-events__heading--metric justify-end': column.numeric }"
        >
          <PlatformLogo
            v-if="column.field !== 'date' && channelFor(column.field)"
            v-bind="logoProps(column.field)"
          />
          {{ column.header }}
        </span>
      </template>
      <template #cell="{ column, value }">
        {{
          column.field === 'date'
            ? format.formatDate(String(value))
            : format.formatNumber(Number(value) || 0)
        }}
      </template>
      <template #footer="{ column, value }">
        {{ column.field === 'date' ? value : format.formatNumber(Number(value) || 0) }}
      </template>
      <template #empty>{{ t('summary.states.empty') }}</template>
      <template #error>
        {{ t('summary.states.error') }}
        <Button size="sm" variant="outline" @click="$emit('retry')">{{
          t('summary.actions.retry')
        }}</Button>
      </template>
    </MatrixTable>
  </div>
</template>
